import {
  FieldSpec,
  FilterGroup,
  FilterLeaf,
  FilterNode,
  SearchCatalog,
  SearchScope,
  isGroup,
} from '../api';

export const EMPTY_GROUP: FilterGroup = { op: 'AND', conditions: [] };

// ─── Public component ─────────────────────────────────────────────────────

export function FilterBuilder({
  scope,
  catalog,
  value,
  onChange,
}: {
  scope: SearchScope;
  catalog: SearchCatalog;
  value: FilterGroup;
  onChange: (next: FilterGroup) => void;
}) {
  const fields = catalog[scope];
  return (
    <GroupView
      depth={0}
      group={value}
      fields={fields}
      onChange={(g) => onChange(g)}
      onRemove={() => onChange(EMPTY_GROUP)}
      removable={false}
    />
  );
}

// ─── Internals ────────────────────────────────────────────────────────────

function GroupView({
  depth,
  group,
  fields,
  onChange,
  onRemove,
  removable,
}: {
  depth: number;
  group: FilterGroup;
  fields: Record<string, FieldSpec>;
  onChange: (g: FilterGroup) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  const bg = depth === 0 ? 'bg-white dark:bg-slate-900' : depth === 1 ? 'bg-slate-50 dark:bg-slate-800/50' : 'bg-white dark:bg-slate-900';
  const setOp = (op: 'AND' | 'OR') => onChange({ ...group, op });
  const updateChild = (i: number, next: FilterNode) => {
    const conditions = [...group.conditions];
    conditions[i] = next;
    onChange({ ...group, conditions });
  };
  const removeChild = (i: number) => {
    const conditions = group.conditions.filter((_, j) => j !== i);
    onChange({ ...group, conditions });
  };
  const addLeaf = () => {
    const firstField = Object.keys(fields)[0];
    if (!firstField) return;
    const spec = fields[firstField];
    const newLeaf: FilterLeaf = {
      field: firstField,
      op: spec.ops[0],
      value: defaultValueFor(spec),
    };
    onChange({ ...group, conditions: [...group.conditions, newLeaf] });
  };
  const addGroup = () => {
    const newGroup: FilterGroup = { op: 'AND', conditions: [] };
    onChange({ ...group, conditions: [...group.conditions, newGroup] });
  };

  return (
    <div className={`rounded border border-slate-200 dark:border-slate-700 ${bg} p-2`}>
      <div className="mb-1 flex items-center gap-2">
        <select
          value={group.op}
          onChange={(e) => setOp(e.target.value as 'AND' | 'OR')}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {group.conditions.length === 0
            ? 'No conditions yet.'
            : `${group.conditions.length} condition${group.conditions.length === 1 ? '' : 's'}`}
        </span>
        <div className="ml-auto flex gap-1">
          <button
            type="button"
            onClick={addLeaf}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + Condition
          </button>
          <button
            type="button"
            onClick={addGroup}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            + Group
          </button>
          {removable && (
            <button
              type="button"
              onClick={onRemove}
              className="rounded border border-rose-200 dark:border-rose-900 bg-white dark:bg-slate-900 px-2 py-0.5 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40"
              title="Remove group"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {group.conditions.map((child, i) =>
          isGroup(child) ? (
            <GroupView
              key={i}
              depth={depth + 1}
              group={child}
              fields={fields}
              onChange={(g) => updateChild(i, g)}
              onRemove={() => removeChild(i)}
              removable={true}
            />
          ) : (
            <LeafView
              key={i}
              leaf={child}
              fields={fields}
              onChange={(l) => updateChild(i, l)}
              onRemove={() => removeChild(i)}
            />
          ),
        )}
      </div>
    </div>
  );
}

function LeafView({
  leaf,
  fields,
  onChange,
  onRemove,
}: {
  leaf: FilterLeaf;
  fields: Record<string, FieldSpec>;
  onChange: (l: FilterLeaf) => void;
  onRemove: () => void;
}) {
  const spec = fields[leaf.field];
  if (!spec) {
    return (
      <div className="flex items-center gap-2 rounded border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-2 py-1 text-xs text-rose-700 dark:text-rose-300">
        Unknown field {leaf.field}.
        <button onClick={onRemove} className="ml-auto hover:underline">remove</button>
      </div>
    );
  }
  const setField = (f: string) => {
    const newSpec = fields[f];
    onChange({ field: f, op: newSpec.ops[0], value: defaultValueFor(newSpec) });
  };
  const setOp = (o: string) => {
    // A bounded field's value changes shape between one choice and a list.
    if (spec.choices) {
      const isList = o === 'in' || o === 'not_in';
      const cur = leaf.value;
      if (isList && !Array.isArray(cur)) {
        onChange({ ...leaf, op: o, value: cur ? [cur] : [] });
        return;
      }
      if (!isList && Array.isArray(cur)) {
        onChange({ ...leaf, op: o, value: cur[0] ?? spec.choices[0]?.value ?? '' });
        return;
      }
    }
    onChange({ ...leaf, op: o });
  };
  const setValue = (v: unknown) => onChange({ ...leaf, value: v });

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded bg-white dark:bg-slate-900 px-2 py-1 text-sm shadow-sm">
      <select
        value={leaf.field}
        onChange={(e) => setField(e.target.value)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      >
        {Object.keys(fields).map((f) => (
          <option key={f} value={f}>{fields[f].label ?? prettyFieldName(f)}</option>
        ))}
      </select>
      <select
        value={leaf.op}
        onChange={(e) => setOp(e.target.value)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      >
        {spec.ops.map((o) => (
          <option key={o} value={o}>{prettyOpName(o)}</option>
        ))}
      </select>
      <ValueEditor spec={spec} op={leaf.op} value={leaf.value} onChange={setValue} />
      <button
        type="button"
        onClick={onRemove}
        className="ml-auto text-xs text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400"
        title="Remove condition"
      >
        ✕
      </button>
    </div>
  );
}

function ValueEditor({
  spec,
  op,
  value,
  onChange,
}: {
  spec: FieldSpec;
  op: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (op === 'is_null' || op === 'is_not_null') return null;
  if (spec.choices) {
    if (op === 'in' || op === 'not_in') {
      const list = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (v: string) =>
        onChange(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
      return (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {spec.choices.map((c) => (
            <label key={c.value} className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={list.includes(c.value)} onChange={() => toggle(c.value)} />
              {c.label}
            </label>
          ))}
        </span>
      );
    }
    const known = spec.choices.some((c) => c.value === value);
    return (
      <select
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      >
        {/* Keep an older saved value visible rather than silently swapping it. */}
        {!known && <option value={(value as string) ?? ''}>{String(value ?? '') || '—'}</option>}
        {spec.choices.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    );
  }
  if (spec.value_type === 'boolean') {
    return (
      <select
        value={String(value ?? 'true')}
        onChange={(e) => onChange(e.target.value === 'true')}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (spec.value_type === 'number') {
    if (op === 'between' && Array.isArray(value)) {
      return (
        <span className="flex items-center gap-1">
          <input
            type="number"
            value={value[0] ?? ''}
            onChange={(e) => onChange([toNum(e.target.value), value[1]])}
            className="w-20 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          />
          <span className="text-xs text-slate-400 dark:text-slate-500">to</span>
          <input
            type="number"
            value={value[1] ?? ''}
            onChange={(e) => onChange([value[0], toNum(e.target.value)])}
            className="w-20 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          />
        </span>
      );
    }
    return (
      <input
        type="number"
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(toNum(e.target.value))}
        className="w-24 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      />
    );
  }
  if (spec.value_type === 'date') {
    if (op === 'in_month') {
      return (
        <select
          value={String(value ?? 1)}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
        >
          {MONTHS.map((m, i) => (
            <option key={i} value={i + 1}>{m}</option>
          ))}
        </select>
      );
    }
    if (op === 'in_year') {
      return (
        <input
          type="number"
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(toNum(e.target.value))}
          className="w-20 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
        />
      );
    }
    if (op === 'older_than_days' || op === 'within_days') {
      return (
        <span className="flex items-center gap-1">
          <input
            type="number"
            min={0}
            value={value == null ? '' : String(value)}
            onChange={(e) => onChange(toNum(e.target.value))}
            className="w-20 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          />
          <span className="text-xs text-slate-400 dark:text-slate-500">days</span>
        </span>
      );
    }
    if (op === 'between' && Array.isArray(value)) {
      return (
        <span className="flex items-center gap-1">
          <input
            type="date"
            value={(value[0] as string) ?? ''}
            onChange={(e) => onChange([e.target.value, value[1]])}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          />
          <span className="text-xs text-slate-400 dark:text-slate-500">to</span>
          <input
            type="date"
            value={(value[1] as string) ?? ''}
            onChange={(e) => onChange([value[0], e.target.value])}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          />
        </span>
      );
    }
    return (
      <input
        type="date"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      />
    );
  }
  if (spec.value_type === 'enum' && (op === 'in' || op === 'not_in')) {
    const list = Array.isArray(value) ? (value as string[]) : [];
    return (
      <input
        type="text"
        value={list.join(', ')}
        placeholder="comma,separated"
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
      />
    );
  }
  // default: text
  return (
    <input
      type="text"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="min-w-[8rem] rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
    />
  );
}

function defaultValueFor(spec: FieldSpec): unknown {
  if (spec.choices?.length) {
    return spec.ops[0] === 'in' || spec.ops[0] === 'not_in' ? [] : spec.choices[0].value;
  }
  switch (spec.value_type) {
    case 'boolean': return true;
    case 'number':  return null;
    case 'date':    return null;
    default:        return '';
  }
}

function toNum(s: string): number | null {
  if (s === '') return null;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? null : n;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function prettyFieldName(f: string): string {
  return f.replace(/_/g, ' ');
}

function prettyOpName(o: string): string {
  switch (o) {
    case 'eq': return '=';
    case 'neq': return '≠';
    case 'gt': return '>';
    case 'gte': return '≥';
    case 'lt': return '<';
    case 'lte': return '≤';
    case 'between': return 'between';
    case 'contains': return 'contains';
    case 'starts_with': return 'starts with';
    case 'ends_with': return 'ends with';
    case 'is_null': return 'is empty';
    case 'is_not_null': return 'is set';
    case 'in_month': return 'in month';
    case 'in_year': return 'in year';
    case 'before': return 'before';
    case 'after':  return 'after';
    case 'in':     return 'in';
    case 'not_in': return 'not in';
    default: return o;
  }
}
